import { VizConfigBuilder } from "@/shared/config/VizConfig";
import { LayoutRecord } from "@/shared/domain/layouts";
import { LayoutDefinition } from "@/shared/domain/layouts/LayoutDefinition";
import { RuleStyle } from "@/shared/domain/layouts/LayoutRecord";
import { TopicNode } from "@/shared/domain/topics";
import { LoggerService } from "@/shared/services";
import { State, TopicData } from "@/shared/state/visualization";
import { Layout, PanelStates } from "@/shared/state/visualization/schema/v1";

import { layoutRuleMatcher, MatchFailureReason } from "./LayoutRuleMatcher";

type MatchedData = { data: TopicData[]; style: RuleStyle };
type Bindings = Record<string, MatchedData[]>;

/**
 * One reason a rule failed to bind. Each of a rule's requirements can fail on
 * its own, so a single rule can account for several of these.
 */
export interface MatchError {
  ruleId: string;
  reason: MatchFailureReason;
}

interface MatchResult {
  bindings: Bindings;
  errors: Record<string, MatchError[]>; // panelId -> errors
}

export interface LayoutToVizConfigContext {
  topicTrees: TopicNode[];
}

/**
 * Builds the visualizer's panels and layout tree from a stored layout, as
 * happens when a user opens a workspace on a layout or switches to another one.
 *
 * A rule that binds to nothing in the workspace does not fail the conversion.
 * The panel is still built, and the failures come back in `errors` keyed by
 * panel id, which is how a panel warns about the data it could not load.
 *
 * @throws {InvalidLayoutDefinitionError} if the definition does not validate against its schema version
 * @throws {UnsupportedLayoutSchemaVersion} if no schema is registered for the layout's schema version
 */
export function layoutToVizConfig(
  layout: LayoutRecord,
  context: LayoutToVizConfigContext,
): {
  panels: { [id: string]: PanelStates };
  layout: State["layout"];
  errors: Record<string, MatchError[]>;
} {
  LoggerService.log("Creating viz state from layout:", layout);

  const clampedLayout = clampRelativeSizes(layout.layout_definition.layout);

  const layoutDefinition = LayoutDefinition.fromObj(
    {
      ...layout.layout_definition,
      layout: clampedLayout,
    },
    layout.schema_version,
  );

  const { bindings, errors } = matchLayoutRules(
    layoutDefinition,
    context.topicTrees,
  );

  const vizConfigBuilder = new VizConfigBuilder().setLayout(clampedLayout);

  layoutDefinition.panels.forEach(({ rules, ...panel }) => {
    const matchedData = rules.map((ruleId) => bindings[ruleId] ?? []).flat();

    const panelData = VizConfigBuilder.transformTopicDataForPanel(
      panel.type,
      panel.id,
      matchedData,
    );

    vizConfigBuilder.addPanel({
      ...panel,
      data: panelData,
    } as PanelStates);
  });

  const vizConfig = vizConfigBuilder.build();

  return {
    panels: vizConfig.panels,
    layout: vizConfig.layout,
    errors,
  };
}

/**
 * Returns a copy of the layout tree in which every `relativeSize`, at every
 * depth, lies in [0, 1], warning for each value it had to change.
 *
 * Rendering multiplies a `relativeSize` by 100 and hands the result to
 * `flex-basis`, so a stored size above 1 sizes a panel past its container and a
 * negative one yields a declaration the browser discards, leaving the panel to
 * size itself from its content. Validating the stored definition catches
 * neither: its schema types `relativeSize` as an unbounded number.
 */
function clampRelativeSizes(layout: Layout): Layout {
  const clampSize = (size: number, id: string, itemType: string): number => {
    const clamped = Math.max(0, Math.min(1, size));
    if (size !== clamped) {
      LoggerService.warn(
        `Clamped ${itemType} relativeSize from ${size} to ${clamped} for ${id}`,
      );
    }
    return clamped;
  };

  const clampedChildren = layout.children.map((child) => {
    if ("children" in child) {
      return clampRelativeSizes(child);
    } else {
      return {
        ...child,
        relativeSize: clampSize(child.relativeSize, child.id, "layout item"),
      };
    }
  });

  return {
    ...layout,
    relativeSize: clampSize(layout.relativeSize, layout.id, "layout"),
    children: clampedChildren,
  };
}

/**
 * Binds every rule in the layout to the workspace's topics.
 *
 * Bindings come back keyed by rule id, since a panel looks up each rule it
 * lists; failures come back keyed by panel id instead.
 */
function matchLayoutRules(
  layoutDefinition: LayoutDefinition,
  topicTrees: TopicNode[],
): MatchResult {
  const bindings: Bindings = {};
  const errors: Record<string, MatchError[]> = {};

  layoutDefinition.rules.forEach((rule) => {
    const { matches, failures } = layoutRuleMatcher.findMatches(
      rule,
      topicTrees,
    );

    if (matches.length > 0) {
      LoggerService.log(
        `Matched rule ${rule.id} with ${matches.length} matches`,
      );
      bindings[rule.id] = matches.map((match) => ({
        data: match.topicData,
        style: rule.style ?? {},
      }));
    }

    if (failures.length > 0) {
      LoggerService.warn(
        `Failed to match rule ${rule.id} with ${failures.length} failures`,
        failures,
      );

      // A rule can be listed by more than one panel, so each of them gets its
      // own copy of the failure.
      layoutDefinition.panels.forEach((panel) => {
        if (panel.rules.includes(rule.id)) {
          errors[panel.id] ??= [];
          errors[panel.id].push(
            ...failures.map((reason) => ({ ruleId: rule.id, reason })),
          );
        }
      });
    }
  });

  return { bindings, errors };
}
